import { getErpTabId } from './erpTabSession';
const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

let adminAuthSession = null;
let refreshInFlight = null;

export class ApiError extends Error {
  constructor(message, status, details) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.details = details;
  }
}

export function configureAdminApiAuth({
  getAccessToken,
  refreshAccessToken,
  onRefreshFailure,
}) {
  adminAuthSession = {
    getAccessToken,
    refreshAccessToken,
    onRefreshFailure,
  };

  return () => {
    adminAuthSession = null;
    refreshInFlight = null;
  };
}

async function refreshAdminAccessToken() {
  if (!adminAuthSession?.refreshAccessToken) {
    throw new Error("Admin authentication refresh is not configured.");
  }

  if (!refreshInFlight) {
    refreshInFlight = adminAuthSession.refreshAccessToken().finally(() => {
      refreshInFlight = null;
    });
  }

  return refreshInFlight;
}

async function request(path, options, hasRetried = false) {
  const {
    method = "GET",
    body,
    accessToken,
    headers = {},
    skipAuthRefresh = false,
    retryOnUnauthorized = true,
    rawResponse = false,
  } = options;

  const isFormData = body instanceof FormData;
  const isCustomerRequest = path.startsWith('/customer-') || path.startsWith('/storefront');
  const managedToken =
    path === "/auth/refresh" || isCustomerRequest ? null : adminAuthSession?.getAccessToken?.();

  const token = managedToken ?? accessToken ?? null;

  const response = await fetch(`${API_BASE_URL}${path}`, {
    method,
    credentials: "include",
    headers: {
      ...(body !== undefined && !isFormData
        ? { "Content-Type": "application/json" }
        : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(['/auth/login','/auth/refresh','/auth/logout'].includes(path) && getErpTabId() ? { 'X-ERP-Tab-Id': getErpTabId() } : {}),
      ...headers,
    },
    ...(body !== undefined
      ? {
          body: isFormData ? body : JSON.stringify(body),
        }
      : {}),
  });

  if (response.ok) {
    if (rawResponse) {
      return response;
    }

    const contentType = response.headers.get("content-type") || "";
    return contentType.includes("application/json") ? response.json() : null;
  }

  const contentType = response.headers.get("content-type") || "";
  const data = contentType.includes("application/json")
    ? await response.json()
    : null;

  const error = new ApiError(
    data?.message || "Something went wrong. Please try again.",
    response.status,
    data,
  );

  const isRefreshRequest = path === "/auth/refresh";

  if (
    response.status !== 401 ||
    hasRetried ||
    skipAuthRefresh ||
    !retryOnUnauthorized ||
    isRefreshRequest ||
    !token ||
    !adminAuthSession || isCustomerRequest
  ) {
    throw error;
  }

  try {
    const newestToken = adminAuthSession.getAccessToken?.();

    if (newestToken && newestToken !== token) {
      return request(
        path,
        {
          ...options,
          accessToken: newestToken,
        },
        true,
      );
    }

    const refreshedToken = await refreshAdminAccessToken();

    return request(
      path,
      {
        ...options,
        accessToken: refreshedToken,
      },
      true,
    );
  } catch (refreshError) {
    if (![401, 403].includes(refreshError.status)) throw refreshError;
    adminAuthSession?.onRefreshFailure?.();

    throw new ApiError(
      "Your session has expired. Please sign in again.",
      401,
      data,
    );
  }
}

export async function apiRequest(path, options = {}) {
  return request(path, options);
}
